import type { ImportPlacemark, LngLat } from '@ftthnms/shared';
import type { FeatureCollection } from 'geojson';
import maplibregl, { type GeoJSONSource, type Map as MlMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../lib/theme';
import { buildStyle, token } from '../map/style';

const TYPE_TOKEN: Record<string, string> = {
  OLT: 'asset-olt',
  SERVER: 'asset-server',
  AP: 'asset-ap',
  ODC: 'asset-odc',
  ODP: 'asset-odp',
  JC: 'asset-jc',
  POLE: 'asset-pole',
  CPE: 'asset-cpe',
};

/** Small map of an import file: points coloured by the type they will become, skipped ones faded. */
export function ImportMap({
  placemarks,
  typeOf,
}: {
  placemarks: ImportPlacemark[];
  typeOf: (p: ImportPlacemark) => string;
}) {
  const { theme } = useTheme();
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const [ready, setReady] = useState(0);

  useEffect(() => {
    if (!el.current) return;
    const map = new maplibregl.Map({
      container: el.current,
      style: buildStyle('theme'),
      center: [107.2, -6.3],
      zoom: 7,
      attributionControl: { compact: true },
      dragRotate: false,
    });
    mapRef.current = map;
    map.on('style.load', () => {
      const color = [
        'match',
        ['get', 'type'],
        ...Object.entries(TYPE_TOKEN).flatMap(([t, v]) => [t, token(v)]),
        token('text-muted'),
      ];
      map.addSource('pm', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'pm-lines',
        type: 'line',
        source: 'pm',
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: {
          'line-color': ['coalesce', ['get', 'color'], token('primary')] as never,
          'line-width': 2.5,
          'line-opacity': ['case', ['get', 'skip'], 0.25, 0.9],
        },
      });
      map.addLayer({
        id: 'pm-points',
        type: 'circle',
        source: 'pm',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 5,
          'circle-color': color as never,
          'circle-stroke-color': token('map-halo'),
          'circle-stroke-width': 1.5,
          'circle-opacity': ['case', ['get', 'skip'], 0.2, 1],
          'circle-stroke-opacity': ['case', ['get', 'skip'], 0.2, 1],
        },
      });
      setReady((r) => r + 1);
    });
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    mapRef.current?.setStyle(buildStyle('theme'), { diff: false });
  }, [theme]);

  // Fit once to everything in the file.
  const fitted = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || ready === 0) return;
    const data: FeatureCollection = {
      type: 'FeatureCollection',
      features: placemarks.map((p) => {
        const type = typeOf(p);
        return {
          type: 'Feature',
          properties: { type, skip: type === 'skip', color: p.color ?? null },
          geometry:
            p.kind === 'point'
              ? { type: 'Point', coordinates: p.coords as LngLat }
              : { type: 'LineString', coordinates: p.coords as LngLat[] },
        };
      }),
    };
    (map.getSource('pm') as GeoJSONSource | undefined)?.setData(data);
    if (!fitted.current && placemarks.length > 0) {
      fitted.current = true;
      const all = placemarks.flatMap((p) =>
        p.kind === 'point' ? [p.coords as LngLat] : (p.coords as LngLat[]),
      );
      const b = new maplibregl.LngLatBounds(all[0]!, all[0]!);
      for (const c of all) b.extend(c);
      map.fitBounds(b, { padding: 40, maxZoom: 17, duration: 0 });
    }
  }, [placemarks, typeOf, ready]);

  return (
    <div
      ref={el}
      style={{
        height: 360,
        borderRadius: 'var(--radius-xl)',
        overflow: 'hidden',
        border: '1px solid var(--border-subtle)',
      }}
      aria-label="Pratinjau lokasi file"
      role="region"
    />
  );
}
